import { useState } from 'react';
import { api, errorMessage } from '../../lib/api.js';
import { useInvalidate, useMutation, useQuery, useQuota } from '../../lib/queries.js';
import {
  Badge, Button, Card, ConfirmDialog, EmptyState, ErrorBox, Input, Loading, Modal,
  PageHeader, Select, Tabs, useToast,
  IconLock, IconEnvelope,
} from '../../components/ui/index.js';
import { ROLE_LABEL, STATUS_LABEL, date, relative } from '../../lib/format.js';
import { hasRole, useAuth, useRole } from '../../lib/auth.js';

interface Member {
  id: string; userId: string; email: string; fullName: string; avatarUrl: string | null;
  role: string; status: string; joinedAt: string | null; lastLoginAt: string | null;
}
interface Invitation { id: string; email: string; role: string; expiresAt: string; createdAt: string }

const ROLE_HELP: Record<string, string> = {
  ADMIN: 'จัดการบล็อก widget และเชิญสมาชิก แต่ลบบริษัทไม่ได้',
  EDITOR: 'เขียน แก้ และเผยแพร่บทความได้ทุกบทความในบริษัท',
  AUTHOR: 'เขียนและแก้เฉพาะบทความของตัวเอง ต้องส่งให้บรรณาธิการตรวจก่อนเผยแพร่',
  VIEWER: 'อ่านอย่างเดียว ดูสถิติได้ แก้อะไรไม่ได้',
};

export function TeamPage() {
  const invalidate = useInvalidate();
  const toast = useToast();
  const role = useRole();
  const me = useAuth((s) => s.user);
  const quota = useQuota();
  const [inviting, setInviting] = useState(false);
  const [removing, setRemoving] = useState<Member | null>(null);
  const [tab, setTab] = useState('members');

  const team = useQuery({
    queryKey: ['members'],
    queryFn: () => api.get<{ data: Member[]; pending: Invitation[] }>('/v1/members'),
  });

  const changeRole = useMutation({
    mutationFn: ({ id, newRole }: { id: string; newRole: string }) =>
      api.patch(`/v1/members/${id}`, { role: newRole }),
    onSuccess: () => { invalidate(['members']); toast('เปลี่ยนสิทธิ์แล้ว'); },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/v1/members/${id}`),
    onSuccess: () => { invalidate(['members'], ['quota']); toast('นำสมาชิกออกแล้ว'); setRemoving(null); },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.del(`/v1/members/invite/${id}`),
    onSuccess: () => { invalidate(['members']); toast('ยกเลิกคำเชิญแล้ว'); },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  if (!hasRole(role, 'ADMIN')) {
    return <EmptyState icon={<IconLock size={22} />} title="ไม่มีสิทธิ์เข้าถึง"
                       description="หน้าจัดการทีมงานเปิดให้สิทธิ์ระดับผู้ดูแล (ADMIN) ขึ้นไปเท่านั้น" />;
  }
  if (team.isLoading) return <Loading />;
  if (team.error) return <ErrorBox error={team.error} onRetry={() => void team.refetch()} />;

  const atLimit = quota.data ? quota.data.members.used >= quota.data.members.max : false;

  return (
    <>
      <PageHeader
        title="ทีมงาน"
        description={quota.data ? `สมาชิก ${quota.data.members.used} จาก ${quota.data.members.max} คนในแพ็กเกจปัจจุบัน` : undefined}
        actions={
          <Button onClick={() => setInviting(true)} disabled={atLimit}
                  title={atLimit ? 'ถึงเพดานสมาชิกของแพ็กเกจแล้ว' : undefined}>+ เชิญสมาชิก</Button>
        }
      />

      <Tabs active={tab} onChange={setTab} tabs={[
        { id: 'members', label: 'สมาชิก', badge: team.data?.data.length },
        { id: 'pending', label: 'รอตอบรับ', badge: team.data?.pending.length },
      ]} />

      <div className="mt-5">
        {tab === 'members' && (
          <Card className="divide-y divide-[var(--line-soft)]">
            {team.data?.data.map((m) => {
              const isMe = m.userId === me?.id;
              return (
                <div key={m.id} className="flex flex-wrap items-center gap-3 px-4 py-3.5">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[var(--sunken)] text-sm font-bold">
                    {m.fullName.trim().charAt(0)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 truncate text-sm font-medium text-[var(--ink)]">
                      {m.fullName}
                      {isMe && <Badge tone="blue">คุณ</Badge>}
                      {m.status !== 'ACTIVE' && <Badge tone="amber">{STATUS_LABEL[m.status] ?? m.status}</Badge>}
                    </p>
                    <p className="truncate text-xs text-[var(--ink-3)]">
                      {m.email}
                      {m.lastLoginAt && ` · เข้าใช้ล่าสุด${relative(m.lastLoginAt)}`}
                    </p>
                  </div>

                  {m.role === 'OWNER' ? (
                    <Badge tone="violet">{ROLE_LABEL.OWNER}</Badge>
                  ) : (
                    <Select
                      value={m.role} className="w-auto"
                      disabled={changeRole.isPending}
                      onChange={(e) => changeRole.mutate({ id: m.id, newRole: e.target.value })}
                    >
                      {['ADMIN', 'EDITOR', 'AUTHOR', 'VIEWER'].map((r) => (
                        <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                      ))}
                    </Select>
                  )}

                  {hasRole(role, 'OWNER') && m.role !== 'OWNER' && (
                    <Button size="sm" variant="ghost" className="text-[var(--bad)]"
                            onClick={() => setRemoving(m)}>นำออก</Button>
                  )}
                </div>
              );
            })}
          </Card>
        )}

        {tab === 'pending' && (
          !team.data?.pending.length ? (
            <EmptyState icon={<IconEnvelope size={22} />} title="ไม่มีคำเชิญที่รอตอบรับ"
                        description="คำเชิญที่ส่งไปแล้วและยังไม่ถูกตอบรับจะแสดงที่นี่" />
          ) : (
            <Card className="divide-y divide-[var(--line-soft)]">
              {team.data.pending.map((inv) => (
                <div key={inv.id} className="flex flex-wrap items-center gap-3 px-4 py-3.5">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-[var(--ink)]">{inv.email}</p>
                    <p className="text-xs text-[var(--ink-3)]">
                      เชิญเมื่อ {date(inv.createdAt)} · หมดอายุ {date(inv.expiresAt)}
                    </p>
                  </div>
                  <Badge tone="gray">{ROLE_LABEL[inv.role] ?? inv.role}</Badge>
                  <Button size="sm" variant="ghost" loading={revoke.isPending}
                          onClick={() => revoke.mutate(inv.id)}>ยกเลิกคำเชิญ</Button>
                </div>
              ))}
            </Card>
          )
        )}
      </div>

      {inviting && <InviteModal onClose={() => setInviting(false)} />}

      <ConfirmDialog
        open={Boolean(removing)} onClose={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing.id)}
        loading={remove.isPending} danger confirmLabel="นำออกจากทีม" title="นำสมาชิกออก?"
        message={<>นำ <strong>{removing?.fullName}</strong> ออกจากบริษัทนี้ใช่หรือไม่? บทความที่เขียนไว้จะยังอยู่ครบ แต่จะเข้าถึงระบบของบริษัทนี้ไม่ได้อีก</>}
      />
    </>
  );
}

function InviteModal({ onClose }: { onClose: () => void }) {
  const invalidate = useInvalidate();
  const toast = useToast();
  const [form, setForm] = useState({ email: '', role: 'AUTHOR' });
  const [error, setError] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: () => api.post('/v1/members/invite', form),
    onSuccess: () => {
      invalidate(['members'], ['quota']);
      toast(`ส่งคำเชิญไปที่ ${form.email} แล้ว`);
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  return (
    <Modal
      open onClose={onClose} title="เชิญสมาชิกเข้าทีม"
      footer={<>
        <Button variant="ghost" onClick={onClose}>ยกเลิก</Button>
        <Button loading={invite.isPending} disabled={!form.email}
                onClick={() => { setError(null); invite.mutate(); }}>ส่งคำเชิญ</Button>
      </>}
    >
      <div className="space-y-4">
        {error && <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
        <Input label="อีเมล" type="email" required autoFocus value={form.email}
               onChange={(e) => setForm({ ...form, email: e.target.value })}
               hint="เราจะส่งลิงก์เข้าร่วมไปที่อีเมลนี้ ลิงก์ใช้ได้ 7 วัน" />
        <Select label="สิทธิ์" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
          {['ADMIN', 'EDITOR', 'AUTHOR', 'VIEWER'].map((r) => (
            <option key={r} value={r}>{ROLE_LABEL[r]}</option>
          ))}
        </Select>
        <div className="rounded-lg bg-[var(--sunken)] px-3.5 py-3 text-sm text-[var(--ink-2)]">
          {ROLE_HELP[form.role]}
        </div>
      </div>
    </Modal>
  );
}
